import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Play, Plus, Check, Star, Tv, Users, BarChart2, Film, ChevronDown, ChevronUp, CalendarDays } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_META = {
  netflix:        { name: 'Netflix',       color: '#E50914' },
  hulu:           { name: 'Hulu',          color: '#1CE783' },
  disney_plus:    { name: 'Disney+',       color: '#113CCF' },
  hbo_max:        { name: 'HBO Max',       color: '#B535F6' },
  amazon_prime:   { name: 'Amazon Prime',  color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',     color: '#aaaaaa' },
  peacock:        { name: 'Peacock',       color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',    color: '#0064FF' },
};

export default function ShowDetail() {
  const { tmdbId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();

  const mediaType = location.state?.media_type || 'tv';

  const [show, setShow] = useState(null);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(false);
  const [expandedSeason, setExpandedSeason] = useState(null);
  const [seasonEpisodes, setSeasonEpisodes] = useState({});
  const [loadingSeason, setLoadingSeason] = useState(null);
  const [userRating, setUserRating] = useState(null);
  const [userReview, setUserReview] = useState('');
  const [hoverRating, setHoverRating] = useState(0);
  const [ratingSaving, setRatingSaving] = useState(false);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [watchlistConnections, setWatchlistConnections] = useState([]);
  const [crossoverList, setCrossoverList] = useState([]);
  const [crossoverDiscovering, setCrossoverDiscovering] = useState(false);
  const [reportedCrossovers, setReportedCrossovers] = useState(new Set());

  useEffect(() => {
    const fetchShow = async () => {
      try {
        setLoading(true);
        setError(false);
        const token = await getToken();
        const [detailRes, ratingRes] = await Promise.all([
          axios.get(`${API}/tmdb/show-detail/${tmdbId}`, {
            params: { media_type: mediaType },
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/rate/tmdb/${tmdbId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: { rating: null, review: '' } })),
        ]);
        setShow(detailRes.data);
        setInWatchlist(detailRes.data.in_watchlist || false);
        setUserRating(ratingRes.data.rating || null);
        setUserReview(ratingRes.data.review || '');
      } catch (err) {
        console.error('ShowDetail fetch error:', err);
        setError(true);
      } finally {
        // Unblock the page immediately — crossover polling runs independently below
        setLoading(false);
      }

      // Fetch crossovers after the page is visible — never block rendering
      try {
        const token2 = await getToken();
        const [connRes, cxRes] = await Promise.allSettled([
          axios.get(`${API}/shows/${tmdbId}/watchlist-connections`, {
            headers: { Authorization: `Bearer ${token2}` },
          }),
          axios.get(`${API}/crossovers/${tmdbId}`, {
            headers: { Authorization: `Bearer ${token2}` },
          }),
        ]);
        if (connRes.status === 'fulfilled') {
          setWatchlistConnections(connRes.value.data.connections || []);
        }
        if (cxRes.status === 'fulfilled') {
          const cxData = cxRes.value.data.crossovers || [];
          setCrossoverList(cxData);
          if (cxData.length === 0) {
            setCrossoverDiscovering(true);
            const pollDelays = [8000, 20000, 40000];
            for (const delay of pollDelays) {
              await new Promise(r => setTimeout(r, delay));
              try {
                const t3 = await getToken();
                const r2 = await axios.get(`${API}/crossovers/${tmdbId}`, {
                  headers: { Authorization: `Bearer ${t3}` },
                });
                const list = r2.data.crossovers || [];
                if (list.length > 0) { setCrossoverList(list); break; }
              } catch {}
            }
            setCrossoverDiscovering(false);
          }
        }
      } catch {}
    };
    fetchShow();
  }, [tmdbId, mediaType, getToken]);

  const handleAdd = async () => {
    if (inWatchlist || !show || adding) return;
    setAdding(true);
    try {
      const token = await getToken();
      const availableProvider = show.providers?.find(p => p.available);
      const service_id = availableProvider?.service_id || 'netflix';
      await axios.post(`${API}/shows`, {
        title: show.name,
        service_id,
        image_url: show.poster_url || '',
        tmdb_id: String(tmdbId),
        media_type: show.media_type || mediaType,
        deep_link: show.justwatch_link || '',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setInWatchlist(true);
    } catch (err) {
      console.error('Add show error:', err);
    }
    setAdding(false);
  };

  const saveRating = async (stars) => {
    setRatingSaving(true);
    try {
      const token = await getToken();
      await axios.post(`${API}/rate/tmdb/${tmdbId}`,
        { rating: stars, review: userReview, media_type: show?.media_type || mediaType, title: show?.title || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserRating(stars);
    } catch {}
    setRatingSaving(false);
  };

  const saveReview = async () => {
    if (!userRating) return;
    setRatingSaving(true);
    try {
      const token = await getToken();
      await axios.post(`${API}/rate/tmdb/${tmdbId}`,
        { rating: userRating, review: userReview, media_type: show?.media_type || mediaType, title: show?.title || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {}
    setRatingSaving(false);
  };

  const toggleSeason = async (seasonNum) => {
    if (expandedSeason === seasonNum) { setExpandedSeason(null); return; }
    setExpandedSeason(seasonNum);
    if (seasonEpisodes[seasonNum]) return;
    setLoadingSeason(seasonNum);
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/tmdb/season/${tmdbId}/${seasonNum}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSeasonEpisodes(prev => ({ ...prev, [seasonNum]: res.data.episodes || [] }));
    } catch { setSeasonEpisodes(prev => ({ ...prev, [seasonNum]: [] })); }
    setLoadingSeason(null);
  };

  const reportCrossover = async (crossoverId) => {
    if (reportedCrossovers.has(crossoverId)) return;
    try {
      const token = await getToken();
      await axios.post(`${API}/crossovers/feedback`,
        { crossover_id: crossoverId, is_correct: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportedCrossovers(prev => new Set([...prev, crossoverId]));
    } catch {}
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !show) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center">
        <Tv className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
        <p className="text-neutral-500 mb-4">Show not found.</p>
        <button onClick={() => navigate(-1)} className="text-violet-400 text-sm hover:text-violet-300 transition-colors">
          ← Go back
        </button>
      </div>
    );
  }

  const availableProviders = (show.providers || []).filter(p => p.available);

  return (
    <div className="max-w-2xl mx-auto pb-12">

      {/* Back */}
      <button onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors mb-4 mt-1">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Hero backdrop */}
      <div className="relative rounded-2xl overflow-hidden mb-6" style={{ minHeight: 160 }}>
        {show.backdrop_url
          ? <img src={show.backdrop_url} alt={show.name} className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0 bg-gradient-to-br from-violet-900/40 to-black" />
        }
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

        <div className="relative p-5 flex items-end" style={{ minHeight: 160 }}>
          <div className="flex gap-4 items-end w-full">
            {show.poster_url && (
              <img src={show.poster_url} alt={show.name}
                className="w-24 h-36 rounded-xl object-cover flex-shrink-0 shadow-xl" />
            )}
            <div className="flex-1 min-w-0 pb-1">
              <h1 className="text-2xl font-black text-white leading-tight mb-2" style={{ fontFamily: 'Outfit' }}>
                {show.name}
              </h1>
              <div className="flex items-center gap-3 flex-wrap mb-3">
                {show.media_type === 'movie' && (
                  <span className="flex items-center gap-1 text-xs text-violet-400 font-semibold">
                    <Film className="w-3 h-3" /> Movie
                  </span>
                )}
                {show.vote_average > 0 && (
                  <span className="flex items-center gap-1 text-xs text-yellow-400 font-semibold">
                    <Star className="w-3 h-3 fill-yellow-400" /> {show.vote_average}
                  </span>
                )}
                {show.first_air_date && (
                  <span className="text-xs text-neutral-400">{show.first_air_date.slice(0, 4)}</span>
                )}
                {show.runtime > 0 && (
                  <span className="text-xs text-neutral-400">{show.runtime}m</span>
                )}
                {show.number_of_seasons > 0 && (
                  <span className="text-xs text-neutral-400">
                    {show.number_of_seasons} season{show.number_of_seasons !== 1 ? 's' : ''}
                  </span>
                )}
                {show.status && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    show.status === 'Returning Series' || show.status === 'Released'
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-neutral-500/20 text-neutral-400'
                  }`}>{show.status}</span>
                )}
              </div>

              {/* Add to watchlist button */}
              <button
                onClick={handleAdd}
                disabled={inWatchlist || adding}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
                  inWatchlist
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'text-white hover:opacity-90'
                }`}
                style={!inWatchlist ? { background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' } : {}}
              >
                {inWatchlist
                  ? <><Check className="w-4 h-4" /> In Watchlist</>
                  : adding
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Adding...</>
                  : <><Plus className="w-4 h-4" /> Add to Watchlist</>
                }
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats row */}
      {(show.finish_rate !== null || show.vote_count > 0 || show.number_of_episodes > 0 || show.runtime > 0) && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          {show.finish_rate !== null && show.finish_rate !== undefined && (
            <div className="rounded-xl bg-[#0A0A0A] border border-white/5 p-3 text-center">
              <div className="flex items-center justify-center mb-1">
                <BarChart2 className="w-3.5 h-3.5 text-orange-400" />
              </div>
              <p className={`text-xl font-black ${
                show.finish_rate >= 80 ? 'text-emerald-400'
                : show.finish_rate >= 50 ? 'text-yellow-400'
                : 'text-red-400'
              }`} style={{ fontFamily: 'Outfit' }}>{show.finish_rate}%</p>
              <p className="text-xs text-neutral-500">Watched</p>
            </div>
          )}
          {show.vote_count > 0 && (
            <div className="rounded-xl bg-[#0A0A0A] border border-white/5 p-3 text-center">
              <p className="text-xl font-black text-yellow-400" style={{ fontFamily: 'Outfit' }}>
                {show.vote_average}
              </p>
              <p className="text-xs text-neutral-500">
                {show.vote_count >= 1000
                  ? `${(show.vote_count / 1000).toFixed(1)}k ratings`
                  : `${show.vote_count} ratings`}
              </p>
            </div>
          )}
          {show.number_of_episodes > 0 && (
            <div className="rounded-xl bg-[#0A0A0A] border border-white/5 p-3 text-center">
              <p className="text-xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
                {show.number_of_episodes}
              </p>
              <p className="text-xs text-neutral-500">Episodes</p>
            </div>
          )}
          {show.runtime > 0 && show.media_type === 'movie' && (
            <div className="rounded-xl bg-[#0A0A0A] border border-white/5 p-3 text-center">
              <p className="text-xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
                {show.runtime}m
              </p>
              <p className="text-xs text-neutral-500">Runtime</p>
            </div>
          )}
        </div>
      )}

      {/* Next episode badge */}
      {show.next_episode && (
        <div className={`flex items-center gap-3 p-4 rounded-xl border mb-5 ${
          show.next_episode.days_until === 0
            ? 'bg-red-500/10 border-red-500/20'
            : show.next_episode.days_until > 0
            ? 'bg-violet-500/10 border-violet-500/20'
            : 'bg-emerald-500/10 border-emerald-500/20'
        }`}>
          <div>
            <p className={`text-sm font-bold ${
              show.next_episode.days_until === 0 ? 'text-red-400'
              : show.next_episode.days_until > 0 ? 'text-violet-400'
              : 'text-emerald-400'
            }`}>
              {show.next_episode.days_until === 0 ? '🔴 New episode today!'
               : show.next_episode.days_until > 0 ? `🗓 Next episode ${show.next_episode.label}`
               : `✨ ${show.next_episode.label}`}
            </p>
            <p className="text-xs text-neutral-400 mt-0.5">
              S{show.next_episode.season}E{show.next_episode.episode} — {show.next_episode.name}
            </p>
          </div>
        </div>
      )}

      {/* Watchlist connections — shows from TMDB recommendations already in your list */}
      {watchlistConnections.length > 0 && (
        <div className="mb-5">
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">You're Also Watching</p>
          <div className="flex flex-col gap-2">
            {watchlistConnections.map(conn => (
              <div key={conn.tmdb_id}
                onClick={() => navigate(`/show/${conn.tmdb_id}`, { state: { media_type: 'tv' } })}
                className="flex items-center gap-3 p-3 rounded-xl bg-violet-500/8 border border-violet-500/15 cursor-pointer hover:bg-violet-500/12 transition-colors">
                <span className="text-violet-400 text-sm font-bold">⚡</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{conn.title}</p>
                  {(conn.their_season || conn.their_episode) && (
                    <p className="text-xs text-neutral-500">
                      S{conn.their_season}E{conn.their_episode} · {conn.their_status === 'watching' ? 'Watching' : conn.their_status}
                    </p>
                  )}
                </div>
                <span className="text-xs text-neutral-600 capitalize">{conn.relationship}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Where to watch — services user has */}
      {availableProviders.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">Where to Watch</p>
          <div className="flex flex-wrap gap-2">
            {availableProviders.map(p => {
              const meta = SERVICE_META[p.service_id];
              const metaColor = meta ? meta.color : null;
              const metaName = meta ? meta.name : p.service_id;
              const bgStyle = metaColor ? metaColor + '20' : 'rgba(255,255,255,0.05)';
              const borderStyle = metaColor ? '1px solid ' + metaColor + '40' : '1px solid rgba(255,255,255,0.1)';
              return (
                <button
                  key={p.service_id}
                  onClick={async () => {
                    const popup = window.open('about:blank', '_blank');
                    try {
                      const token = await getToken();
                      const params = new URLSearchParams({
                        tmdb_id: String(tmdbId),
                        service_id: p.service_id,
                        media_type: show.media_type || mediaType,
                        title: show.name || '',
                      });
                      const res = await axios.get(`${API}/launch-url-v2?${params}`, {
                        headers: { Authorization: `Bearer ${token}` },
                        timeout: 8000,
                      });
                      const url = res.data?.url;
                      if (url && popup) { popup.location.href = url; return; }
                    } catch {}
                    if (popup) popup.location.href = show.justwatch_link || 'https://www.justwatch.com';
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 cursor-pointer"
                  style={{ background: bgStyle, border: borderStyle }}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: metaColor || '#fff' }} />
                  {metaName}
                  <Play className="w-3 h-3" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Affiliate links — services user doesn't have */}
      {show.providers && show.providers.filter(p => !p.available).length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">Also available on</p>
          <div className="flex flex-wrap gap-2">
            {show.providers.filter(p => !p.available).map(p => {
              const meta = SERVICE_META[p.service_id];
              const metaColor = meta ? meta.color : '#555';
              const metaName = meta ? meta.name : p.service_id;
              return (
                <a key={p.service_id}
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/affiliate/${p.service_id}?tmdb_id=${tmdbId}`}
                  target="_blank" rel="noopener noreferrer"
                  onClick={async (e) => {
                    // Track click via backend then navigate
                    e.preventDefault();
                    try {
                      const { data } = await axios.get(
                        `${API}/affiliate/${p.service_id}?tmdb_id=${tmdbId}`,
                        { headers: { Authorization: `Bearer ${await getToken()}` } }
                      );
                      window.open(data.url, '_blank');
                    } catch {
                      window.open(show.justwatch_link || `https://www.justwatch.com`, '_blank');
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-neutral-400 hover:text-white transition-all border border-white/8 hover:border-white/20"
                  style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 opacity-60"
                    style={{ backgroundColor: metaColor }} />
                  Try {metaName}
                  <span className="text-xs opacity-50">→</span>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Overview */}
      {show.overview && (
        <div className="mb-6">
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-2">Overview</p>
          <p className="text-sm text-neutral-300 leading-relaxed">{show.overview}</p>
        </div>
      )}

      {/* Your Rating */}
      <div className="mb-6 p-4 rounded-2xl bg-[#0A0A0A] border border-white/5">
        <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">Your Rating</p>
        <div className="flex items-center gap-1 mb-3"
          onMouseLeave={() => setHoverRating(0)}>
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              onClick={() => saveRating(n)}
              onMouseEnter={() => setHoverRating(n)}
              disabled={ratingSaving}
              className="p-0.5 transition-transform hover:scale-110 disabled:opacity-50"
            >
              <Star
                className={`w-7 h-7 transition-colors ${
                  n <= (hoverRating || userRating || 0)
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-neutral-700 hover:text-neutral-500'
                }`}
              />
            </button>
          ))}
          {userRating && (
            <span className="text-sm text-neutral-500 ml-2">
              {['', 'Poor', 'Fair', 'Good', 'Great', 'Amazing'][userRating]}
            </span>
          )}
        </div>
        {userRating && (
          <input
            type="text"
            placeholder="Add a short review… (optional)"
            value={userReview}
            onChange={e => setUserReview(e.target.value)}
            onBlur={saveReview}
            maxLength={200}
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-violet-500/40"
          />
        )}
      </div>

      {/* Crossover episodes — shown early so users don't have to scroll past episode list */}
      {crossoverDiscovering && crossoverList.length === 0 && (
        <div className="flex items-center gap-2 mb-4 text-xs text-neutral-600">
          <div className="w-3 h-3 border border-neutral-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          Looking for crossover episodes…
        </div>
      )}
      {crossoverList.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">Crossover Episodes</p>
          <div className="flex flex-col gap-2">
            {crossoverList.map(cx => (
              <div key={cx.crossover_id}
                className="p-3 rounded-xl bg-[#0A0A0A] border border-white/5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono text-violet-400 font-bold">
                        S{cx.season}E{cx.episode}
                      </span>
                      <span className="text-xs text-neutral-600">×</span>
                      <button
                        onClick={() => navigate(`/show/${cx.show_b_tmdb_id}`, { state: { media_type: 'tv' } })}
                        className="text-xs font-semibold text-white hover:text-violet-400 transition-colors text-left">
                        {cx.show_b_title}
                      </button>
                      <span className="text-xs font-mono text-neutral-500 flex-shrink-0">
                        S{cx.crossover_b_season}E{cx.crossover_b_episode}
                      </span>
                    </div>
                    {cx.description && (
                      <p className="text-xs text-neutral-500 leading-relaxed">{cx.description}</p>
                    )}
                    {cx.franchise && (
                      <span className="inline-block mt-1.5 text-xs px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20">
                        {cx.franchise}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => reportCrossover(cx.crossover_id)}
                    disabled={reportedCrossovers.has(cx.crossover_id)}
                    className="flex-shrink-0 text-xs text-neutral-700 hover:text-neutral-400 transition-colors disabled:opacity-40 mt-0.5"
                    title="Not a crossover?">
                    {reportedCrossovers.has(cx.crossover_id) ? 'Reported' : '✕'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Genres */}
      {show.genres?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {show.genres.map(g => (
            <span key={g.id}
              className="text-xs px-3 py-1 rounded-full border border-white/10 text-neutral-400">
              {g.name}
            </span>
          ))}
        </div>
      )}

      {/* Cast */}
      {show.cast?.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">Cast</p>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {show.cast.map(person => (
              <div key={person.id} className="flex-shrink-0 w-16 text-center">
                <div className="w-16 h-16 rounded-full bg-white/5 overflow-hidden mx-auto mb-1.5">
                  {person.profile_url
                    ? <img src={person.profile_url} alt={person.name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center">
                        <Users className="w-6 h-6 text-neutral-600" />
                      </div>
                  }
                </div>
                <p className="text-xs text-white font-semibold truncate leading-tight">{person.name}</p>
                <p className="text-xs text-neutral-600 truncate">{person.character}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Network */}
      {show.networks?.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-2">Network</p>
          <p className="text-sm text-neutral-300">{show.networks.map(n => n.name).join(', ')}</p>
        </div>
      )}

      {/* Season/Episode browser — TV only */}
      {show.media_type !== 'movie' && show.number_of_seasons > 0 && (
        <div className="mb-6">
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">Episodes</p>
          <div className="space-y-2">
            {Array.from({ length: show.number_of_seasons }, (_, i) => i + 1).map(seasonNum => (
              <div key={seasonNum} className="rounded-xl bg-[#0A0A0A] border border-white/5 overflow-hidden">
                <button
                  onClick={() => toggleSeason(seasonNum)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/3 transition-colors"
                >
                  <span className="text-sm font-semibold text-white">Season {seasonNum}</span>
                  <div className="flex items-center gap-2">
                    {loadingSeason === seasonNum && (
                      <div className="w-3.5 h-3.5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                    )}
                    {expandedSeason === seasonNum
                      ? <ChevronUp className="w-4 h-4 text-neutral-500" />
                      : <ChevronDown className="w-4 h-4 text-neutral-500" />
                    }
                  </div>
                </button>
                {expandedSeason === seasonNum && (
                  <div className="border-t border-white/5">
                    {(seasonEpisodes[seasonNum] || []).length === 0 && loadingSeason !== seasonNum ? (
                      <p className="px-4 py-3 text-xs text-neutral-600">No episodes found.</p>
                    ) : (
                      (seasonEpisodes[seasonNum] || []).map(ep => {
                        const aired = ep.air_date && new Date(ep.air_date) <= new Date();
                        return (
                          <div key={ep.episode_number}
                            className="flex items-start gap-3 px-4 py-3 border-b border-white/5 last:border-0">
                            <span className="text-xs font-mono text-neutral-600 w-8 flex-shrink-0 mt-0.5">
                              E{ep.episode_number}
                            </span>
                            {ep.still_url && (
                              <img src={ep.still_url} alt={ep.name}
                                className="w-16 h-9 rounded-lg object-cover flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-semibold truncate ${aired ? 'text-white' : 'text-neutral-500'}`}>
                                {ep.name}
                              </p>
                              {ep.air_date && (
                                <p className="text-xs text-neutral-600 flex items-center gap-1 mt-0.5">
                                  <CalendarDays className="w-3 h-3" />
                                  {ep.air_date}
                                  {!aired && <span className="text-violet-400 ml-1">· Upcoming</span>}
                                </p>
                              )}
                              {ep.overview && (
                                <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{ep.overview}</p>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Similar shows */}
      {show.similar?.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">Similar Shows</p>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {show.similar.map(s => (
              <div key={s.tmdb_id}
                onClick={() => navigate(`/show/${s.tmdb_id}`, { state: { media_type: show.media_type || 'tv' } })}
                className="flex-shrink-0 w-24 cursor-pointer group">
                <div className="w-24 h-36 rounded-xl bg-white/5 overflow-hidden mb-2">
                  {s.poster_url
                    ? <img src={s.poster_url} alt={s.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                    : <div className="w-full h-full flex items-center justify-center">
                        <Tv className="w-6 h-6 text-neutral-600" />
                      </div>
                  }
                </div>
                <p className="text-xs text-white font-semibold truncate">{s.name}</p>
                {s.vote_average > 0 && (
                  <p className="text-xs text-neutral-500">⭐ {s.vote_average}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
